import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { Alert, AlertFilter, AlertLevel } from '@/types/fcc'
import { filterAlert } from '@/util/fcc/FCCUtil'
import { useFCCApi } from '@/services/fraudCommandCenterService'
import { AuthState } from '@/types/authState'
import { FCCApiParams } from '@/types/fcc'
import { ApiError } from '@/util/ApiError'

export const useFCCAlertStore = defineStore('fccAlert', () => {
  // State
  const alerts = ref<Alert[]>([])
  const selectedAlerts = ref<Alert[]>([])
  const loading = ref(false)
  const error = ref<ApiError | null>(null)
  const selectedLayout = ref<'table' | 'list'>(
    (sessionStorage.getItem('fccAlertsLayout') as 'table' | 'list') || 'table',
  )
  const availableLayouts = ref<('table' | 'list')[]>(['table', 'list'])
  const currentAlert = ref<Alert | null>(null)

  // Request deduplication
  const fetchAlertsPromise = ref<Promise<Alert[]> | null>(null)

  const filters = ref<AlertFilter>({
    eventType: {
      selected: [],
      options: [],
    },
    alertLevel: {
      selected: [],
      options: [
        { value: AlertLevel.HIGH, label: 'High' },
        { value: AlertLevel.MEDIUM, label: 'Medium' },
        { value: AlertLevel.LOW, label: 'Low' },
      ],
    },
    significance: {
      min: 0,
      max: 1000000,
    },
    search: {
      value: '',
    },
  })

  const minMaxSignificance = ref({
    min: 0,
    max: 1000000,
  })

  // API utilities
  const { getAlerts } = useFCCApi()

  // Getters
  const actualMinMaxSignificance = computed(() => {
    if (!Array.isArray(alerts.value) || alerts.value.length === 0) {
      return { min: 0, max: 1000000 }
    }

    const significanceValues = alerts.value
      .map(alert => alert.SIGNIFICANCE)
      .filter(val => typeof val === 'number' && !isNaN(val))

    if (significanceValues.length === 0) {
      return { min: 0, max: 1000000 }
    }

    return {
      min: Math.min(...significanceValues),
      max: Math.max(...significanceValues),
    }
  })

  const filteredAlerts = computed(() => {
    if (!Array.isArray(alerts.value)) {
      return []
    }
    return alerts.value.filter(alert => filterAlert(alert, filters.value))
  })

  // Actions
  const setAlerts = (newAlerts: Alert[]) => {
    alerts.value = Array.isArray(newAlerts) ? newAlerts : []
    minMaxSignificance.value = actualMinMaxSignificance.value
  }

  const setCurrentAlert = (alert: Alert | null) => {
    currentAlert.value = alert
  }

  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading
  }

  const setSelectedLayout = (layout: 'table' | 'list') => {
    selectedLayout.value = layout
    sessionStorage.setItem('fccAlertsLayout', layout)
  }

  const updateFilters = (newFilters: Partial<AlertFilter>) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const resetFilters = () => {
    const actualMinMax = actualMinMaxSignificance.value
    filters.value = {
      eventType: {
        selected: [],
        options: filters.value.eventType.options,
      },
      alertLevel: {
        selected: [],
        options: filters.value.alertLevel.options,
      },
      significance: {
        min: actualMinMax.min,
        max: actualMinMax.max,
      },
      search: {
        value: '',
      },
    }
  }

  const fetchAlerts = async (
    authState: AuthState,
    params?: FCCApiParams,
    forceRefresh = false,
  ) => {
    // If there's already a request in progress and we're not forcing a refresh, return the existing promise
    if (fetchAlertsPromise.value && !forceRefresh) {
      return fetchAlertsPromise.value
    }

    // Create a new promise for the request
    fetchAlertsPromise.value = (async () => {
      loading.value = true
      error.value = null
      try {
        const fetchedAlerts = await getAlerts(authState, params)
        alerts.value = fetchedAlerts
        return fetchedAlerts
      } catch (err) {
        error.value = ApiError.isApiError(err) ? err : null
        alerts.value = []
        throw err
      } finally {
        loading.value = false
        // Clear the promise reference when done
        fetchAlertsPromise.value = null
      }
    })()

    return fetchAlertsPromise.value
  }

  const clearAlerts = () => {
    alerts.value = []
    currentAlert.value = null
  }

  const clearSelectedAlerts = () => {
    selectedAlerts.value = []
  }

  const refreshAlerts = async (authState: AuthState, params?: FCCApiParams) => {
    return fetchAlerts(authState, params, true)
  }

  return {
    // State
    alerts,
    selectedAlerts,
    loading,
    error,
    selectedLayout,
    availableLayouts,
    currentAlert,
    filters,
    minMaxSignificance,

    // Getters
    actualMinMaxSignificance,
    filteredAlerts,

    // Actions
    setAlerts,
    setCurrentAlert,
    setLoading,
    setSelectedLayout,
    updateFilters,
    resetFilters,
    fetchAlerts,
    refreshAlerts,
    clearAlerts,
    clearSelectedAlerts,
  }
})
